export type ScopingQuestionType =
  | "short_text"
  | "long_text"
  | "email"
  | "single_choice"
  | "multi_choice"
  | "dropdown"
  | "yes_no"
  | "target_builder";

export type ScopingQuestionOption = { value: string; label: string; flags?: string[] };

export type ScopingQuestion = {
  qid: string;
  label: string;
  type: ScopingQuestionType | string;
  required?: boolean;
  options?: ScopingQuestionOption[] | string[];
};

export type ScopingSection = {
  id: string;
  title: string;
  description?: string;
  questions: ScopingQuestion[];
};

export type ScopingSchema = {
  sections: ScopingSection[];
};

/** Normalize legacy string options to { value, label } objects for the editor. */
export function normalizeOptions(
  options?: ScopingQuestionOption[] | string[]
): ScopingQuestionOption[] {
  if (!options?.length) return [];
  return options.map((opt) => {
    if (opt === null || opt === undefined) {
      return { value: "", label: "" };
    }
    if (typeof opt === "string") {
      return { value: opt, label: opt };
    }
    return {
      value: opt.value !== undefined ? String(opt.value) : "",
      label: opt.label !== undefined ? String(opt.label) : "",
      flags: Array.isArray(opt.flags) ? opt.flags.map(String) : undefined
    };
  });
}

export const createDefaultSchema = (): ScopingSchema => ({
  sections: [
    {
      id: `section_${Date.now()}`,
      title: "Section 1: Contact details",
      description: "Company and contact information.",
      questions: [
        {
          qid: "client_name",
          label: "Company name",
          type: "short_text",
          required: true
        }
      ]
    }
  ]
});

export function validateScopingSchema(schema: ScopingSchema | null): string[] {
  const errors: string[] = [];
  if (!schema?.sections?.length) {
    errors.push("Add at least one section.");
    return errors;
  }

  const qids = new Set<string>();
  schema.sections.forEach((sec, si) => {
    if (!sec.title?.trim()) {
      errors.push(`Section ${si + 1} needs a title.`);
    }
    (sec.questions || []).forEach((q, qi) => {
      if (!q.label?.trim()) {
        errors.push(`Section ${si + 1}, question ${qi + 1}: label is required.`);
      }
      const key = q.qid?.trim();
      if (!key) {
        errors.push(`Section ${si + 1}, question ${qi + 1}: variable key is required.`);
      } else if (qids.has(key)) {
        errors.push(`Duplicate variable key "${key}". Keys must be unique.`);
      } else {
        qids.add(key);
      }
      const isChoice =
        q.type === "single_choice" || q.type === "multi_choice" || q.type === "dropdown";
      if (isChoice && !(q.options?.length)) {
        errors.push(`"${q.label || key}" needs at least one choice option.`);
      }
    });
  });

  return errors;
}
